'use client'

/**
 * Workspace-level Smart Spend toggle.
 *
 * Shown at the top of /budget's allocation section. When ON, the nightly
 * engine manages all eligible channel budgets holistically and rows become
 * read-only. When OFF, fully manual.
 *
 * On enable: shows a confirmation Dialog (proper z-[120], above sidebars).
 * On disable: flips immediately with no confirm.
 *
 * z-index fix 2026-05-21: replaced hand-rolled `fixed inset-0 z-50` modal
 * with the shared Dialog primitive so it renders above sidebars and the
 * sticky page header without fighting z-index wars.
 *
 * Spec: ~/.openclaw/workspace/specs/smart-spend-v1-spec.md
 * Stephen-approved: 2026-05-21
 */

import { useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { SparklesIcon } from '@hugeicons-pro/core-stroke-rounded'
import { AiMagicIcon as AiMagicSolidIcon } from '@hugeicons-pro/core-solid-rounded'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

interface Props {
  enabled: boolean
  /** Called after a successful toggle. Receives the new value. */
  onChange?: (next: boolean) => void | Promise<void>
  workspaceId: string
  /** `inline` — compact row for section headers; `card` — bordered panel (default). */
  variant?: 'card' | 'inline'
}

export function SmartSpendWorkspaceToggle({
  enabled,
  onChange,
  workspaceId,
  variant = 'card',
}: Props) {
  const [optimistic, setOptimistic] = useState(enabled)
  const [busy, setBusy] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const toggle = async (next: boolean) => {
    if (busy) return
    if (next) {
      // Show confirmation dialog before enabling.
      setShowConfirm(true)
      return
    }
    // Disable: no confirm needed.
    await apply(false)
  }

  const apply = async (next: boolean) => {
    setShowConfirm(false)
    setOptimistic(next)
    setBusy(true)
    try {
      const res = await fetch('/api/smart-spend/workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next, workspace_id: workspaceId }),
      })
      if (!res.ok) {
        setOptimistic(!next)
      } else if (onChange) {
        await onChange(next)
      }
    } catch {
      setOptimistic(!next)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {/* Toggle row.
          Stephen 2026-05-23 polish:
          - solid AiMagicIcon next to "Smart allocation" (used to be stroke Sparkles)
          - label text is font-medium in all variants
          - clicking the label (icon + text region) triggers the switch toggle
          - switch sized up a notch via className override
      */}
      <div
        className={cn(
          'flex items-center gap-2.5 shrink-0',
          variant === 'card' && [
            'mb-4 justify-between rounded-xl border px-4 py-3 transition-colors',
            optimistic
              ? 'border-accent-300 bg-accent-50/60'
              : 'border-atrium-border bg-atrium-inner',
          ],
          variant === 'inline' && 'justify-end',
        )}
      >
        {/* Label region (icon + text) — click toggles the switch. */}
        <button
          type="button"
          onClick={() => void toggle(!optimistic)}
          disabled={busy}
          aria-label={
            optimistic
              ? 'Smart allocation is on — click to disable and return to manual'
              : 'Click to enable smart allocation for all channels'
          }
          className={cn(
            'flex items-center gap-2.5 rounded-md transition-colors -my-0.5 py-0.5 hover:bg-atrium-content/50 disabled:cursor-not-allowed disabled:opacity-50',
            variant === 'card' && 'flex-1 min-w-0 -mx-1 px-1 text-left',
            variant === 'inline' && '-mx-1 px-1',
          )}
        >
          <HugeiconsIcon
            icon={AiMagicSolidIcon}
            className={cn(
              'size-4 shrink-0',
              optimistic ? 'text-accent-600' : 'text-atrium-muted',
            )}
          />
          <div className={cn(variant === 'card' && 'flex-1 min-w-0')}>
            <div
              className={cn(
                'text-sm font-medium',
                variant === 'inline' && (optimistic ? 'text-atrium-black' : 'text-atrium-muted'),
                variant === 'card' && (optimistic ? 'text-accent-800' : 'text-atrium-black'),
              )}
            >
              Smart allocation
            </div>
            {variant === 'card' && (
              <div className="mt-0.5 text-[11px] font-normal text-atrium-muted">
                {optimistic
                  ? 'Managing all eligible channels — allocations are read-only.'
                  : 'Off — allocations are fully manual.'}
              </div>
            )}
          </div>
        </button>

        {/* Slightly bigger switch. Default Switch is h-[1.15rem] w-8 / size-4 thumb.
            Bump to h-[1.35rem] w-10 / size-5 thumb. Thumb translate is computed
            from track width via `translate-x-[calc(100%-2px)]` so it scales
            automatically. */}
        <Switch
          checked={optimistic}
          onCheckedChange={(next) => void toggle(next)}
          disabled={busy}
          className="h-[1.35rem] w-10 [&>[data-slot=switch-thumb]]:size-5"
          aria-label={
            optimistic
              ? 'Smart allocation is on — click to disable and return to manual'
              : 'Click to enable smart allocation for all channels'
          }
        />
      </div>

      {/* Confirmation dialog — uses Dialog primitive (z-[120], above sidebar) */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-accent-700">
              <HugeiconsIcon icon={SparklesIcon} className="size-5 shrink-0" />
              Enable smart allocation?
            </DialogTitle>
            <DialogDescription>
              Smart allocation will manage budget allocations across{' '}
              <strong className="text-atrium-black">all your connected channels</strong>. The
              nightly engine rebalances spend toward what&apos;s performing best. Channel
              budget rows will become read-only while smart allocation is on.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setShowConfirm(false)}
              className="rounded-lg border border-atrium-border bg-white px-4 py-2 text-sm text-atrium-black hover:bg-atrium-content transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void apply(true)}
              className="rounded-lg bg-accent-500 px-4 py-2 text-sm font-medium text-white hover:bg-accent-600 transition-colors"
            >
              Enable smart allocation
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
